import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import halaqaService from '../../services/halaqaService';
import AuthContext from '../../contexts/AuthContext';
import '../../styles/classes/HalaqaStudentsPage.css';

const HalaqaStudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  
  // Get halaqaId from either params or location state
  const halaqaId = id || (location.state && location.state.halaqaId);
  const halaqaName = location.state && location.state.halaqaName;
  const centerId = location.state && location.state.centerId || user.center;
  
  useEffect(() => {
    // Verify we have the required parameters
    if (!halaqaId) {
      console.error('No Halaqa ID provided');
      navigate('/classes');
      return;
    }

    const fetchHalaqaStudents = async () => {
      try {
        console.log(`Fetching students for Halaqa ID: ${halaqaId}`);
        const studentsData = await halaqaService.getHalaqaStudents(halaqaId);
        console.log('Fetched Students:', studentsData);
        setStudents(studentsData);
      } catch (error) {
        console.error('Error fetching halaqa students:', error);
        // Show error message to user
      } finally {
        setLoading(false);
      }
    };

    fetchHalaqaStudents();
  }, [halaqaId, navigate]);

  // Filter students based on search query
  const filteredStudents = students.filter(student => 
    student.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewProfile = (student) => {
    navigate(`/students/${student._id}`, {
      state: {
        studentName: student.name,
        centerId: centerId
      }
    });
  };

  const handleTakeAttendance = () => {
    navigate('/attendance', {
      state: {
        halaqaId,
        halaqaName,
        centerId: centerId || user.center
      }
    });
  };

  const handleViewClassReport = () => {
    navigate(`/reports/class/${halaqaId}`, {
      state: {
        halaqaName
      }
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading students...</p>
      </div>
    );
  }

  return (
    <div className="halaqa-students-page">
      <div className="page-header">
        <h1>{halaqaName || 'Class'} Students</h1>
        <div className="header-actions">
          <Link to="/dashboard" className="back-button">
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      <div className="search-and-actions">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search students..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="action-buttons">
          <button 
            className="action-button primary"
            onClick={handleTakeAttendance}
          >
            Take Attendance
          </button>
          
          <button 
            className="action-button secondary"
            onClick={handleViewClassReport}
          >
            View Class Report
          </button>
        </div>
      </div>

      <div className="students-stats">
        <div className="stat-card">
          <h3>Total Students</h3>
          <div className="stat-value">{filteredStudents.length}</div>
        </div>
      </div>

      <div className="students-list">
        {filteredStudents.length > 0 ? (
          filteredStudents.map(student => (
            <div 
              key={student._id} 
              className="student-card"
              onClick={() => handleViewProfile(student)}
            >
              <div className="student-avatar">
                {student.name ? student.name.charAt(0).toUpperCase() : '?'}
              </div>
              
              <div className="student-info">
                <h3 className="student-name">{student.name}</h3>
              </div>
              
              <div className="student-actions">
                <button 
                  className="view-profile-btn"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click from firing
                    handleViewProfile(student);
                  }}
                >
                  View Profile
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No students found in this class.</p>
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
              >
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HalaqaStudentsPage;